import { Coins, Copy, Upload } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import DashboardTab from '../../../../../planner/client/dashboard/DashboardTab';
import CustomCheckbox from '../../../../../../components/ui/checkboxes/CustomCheckbox';
import { formatCurrency } from '../../../../../../utils/formatters';
import NumberInput from '../../../../../../components/ui/inputs/NumberInput';
import SimpleInfo from '../components/SimpleInfo';
import CustomButton from '../../../../../../components/ui/buttons/CustomButton';
import { toBlob } from 'html-to-image';
import { useToast } from '../../../../../../components/ui/toast/ToastContext';

const GRID = '30px 100px 70px 1fr 1fr 1fr 100px 1fr';

const SaleRow = ({ position, idx, isSelected, onToggle, onQuantityToSellChange }) => {
  const isBlueRow = idx % 2 === 0;

  const situationColor = (situation) => {
    switch (situation) {
      case 'wc':
        return 'text-greenBright';
      case 'w':
        return 'text-purpleBright';
      case 'c':
        return 'text-orangeBright';
      default:
        return '';
    }
  };

  const profitTextColor = (value) => {
    if (value > 0) return 'text-greenBright';
    if (value < 0) return 'text-redDeep';
    return '';
  };

  return (
    <div className={`${isBlueRow ? 'bg-blueLight' : 'bg-white'} rounded-xl`}>
      <div className="grid items-center gap-4 px-3 py-3 text-sm text-blueDarker" style={{ gridTemplateColumns: GRID }}>
        <CustomCheckbox checked={isSelected} onChange={() => onToggle(position.id)} />

        <div className={`truncate font-semibold ${situationColor(position.situation)}`} title={position.ticker}>
          {position.ticker}
        </div>

        <div className="truncate" title={position.tSubtype}>
          {position.tSubtype}
        </div>

        <div className="truncate" title={formatCurrency(position.marketPrice)}>
          {formatCurrency(position.marketPrice)}
        </div>

        <div className="truncate" title={formatCurrency(position.totalSale)}>
          {formatCurrency(position.totalSale)}
        </div>

        <div
          className={`truncate ${profitTextColor(position.profitSale)} font-semibold`}
          title={formatCurrency(position.profitSale)}
        >
          {formatCurrency(position.profitSale)}
        </div>

        <div>
          <NumberInput
            name={`quantity-to-sell-${position.id}`}
            value={position.quantityToSell}
            onChange={(e) => onQuantityToSellChange(position.id, e.target.value)}
            py="py-2"
            textSize="text-sm"
            decimalScale={0}
            maxValue={position.maxQuantityToSell}
          />
        </div>

        <div></div>
      </div>
    </div>
  );
};

const CopiedTooltip = ({ visible, message }) => {
  if (!visible) return null;

  return (
    <div className="absolute top-full left-1/2 -translate-x-1/2 transform mt-2 bg-blueDarker text-white text-xs px-3 py-1 rounded z-10 whitespace-nowrap">
      {message}
      <div className="absolute -top-1 left-1/2 -translate-x-1 transform w-2 h-2 bg-blueDarker rotate-45"></div>
    </div>
  );
};

const SalesPlanning = ({ equityPositions = [], accountNumber, clientName }) => {
  const [activeTab, setActiveTab] = useState('custom');
  const [planningResults, setPlanningResults] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isCopyingOutput, setIsCopyingOutput] = useState(false);
  const [copiedOutput, setCopiedOutput] = useState(null);
  const [minLossMargin, setMinLossMargin] = useState(500);

  const { addToast } = useToast();

  useEffect(() => {
    fetch('/app/analyst/global_settings', {
      headers: { Accept: 'application/json' },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.settings && data.settings.sales_planning_min_loss_margin != null) {
          setMinLossMargin(Number(data.settings.sales_planning_min_loss_margin));
        }
      })
      .catch(() => {});
  }, []);

  const visibleIds = useMemo(() => {
    return planningResults.map((position) => position.id);
  }, [planningResults]);

  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));

  const someSelected = visibleIds.some((id) => selectedIds.includes(id)) && !allSelected;

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((selectedId) => selectedId !== id);
      }

      return [...prev, id];
    });
  };

  const toggleAll = () => {
    setSelectedIds((prev) => {
      const allVisibleSelected = visibleIds.every((id) => prev.includes(id));

      if (allVisibleSelected) {
        return prev.filter((id) => !visibleIds.includes(id));
      }

      return [...new Set([...prev, ...visibleIds])];
    });
  };

  const handleQuantityToSellChange = (positionId, value) => {
    const parsedValue = value || 0;

    if (activeTab !== 'custom') {
      setActiveTab('custom');
    }

    setPlanningResults((prev) =>
      prev.map((position) => {
        if (position.id !== positionId) return position;

        return {
          ...position,
          quantityToSell: parsedValue,
          totalSale: parsedValue * Number(position.marketPrice || 0),
          profitSale: (Number(position.marketPrice || 0) - Number(position.averagePrice || 0)) * parsedValue,
        };
      })
    );
  };

  const tabs = [
    { key: 'custom', label: 'PERSONALIZADO' },
    { key: 'exemption_20k', label: 'ISENÇÃO 20K' },
    { key: 'offset_loss', label: 'COMPENSAR PREJUÍZO' },
    { key: 'sell_priced', label: 'VENDER PRECIFICADOS' },
    { key: 'liquidate_position', label: 'LIQUIDAR POSIÇÃO' },
  ];

  const calculateEstimatedTax = (positions) => {
    // 1. Separar os ativos por regras de tributação
    const stocks = positions.filter((p) => p.subtype.toLowerCase() === 'stock');
    const commonAssets = positions.filter((p) => ['etf', 'bdr', 'etf_rf'].includes(p.subtype.toLowerCase()));
    const realEstateAssets = positions.filter((p) => ['fii', 'fiagro'].includes(p.subtype.toLowerCase()));

    // 2. Regra das Ações (Isenção 20k)
    const totalStocksSales = stocks.reduce((sum, p) => sum + p.totalSale, 0);
    const totalStocksProfit = stocks.reduce((sum, p) => sum + p.profitSale, 0);

    let stocksTax = 0;
    // Só tributa ações se a venda total ultrapassar 20k E houver lucro líquido
    if (totalStocksSales > 20000 && totalStocksProfit > 0) {
      stocksTax = totalStocksProfit * 0.15;
    }

    // 3. Regra de ETFs e BDRs (15% fixo sobre o lucro, sem isenção)
    const totalCommonProfit = commonAssets.reduce((sum, p) => sum + p.profitSale, 0);
    const commonTax = totalCommonProfit > 0 ? totalCommonProfit * 0.15 : 0;

    // 4. Regra de FIIs e FIAGRO (20% fixo sobre o lucro)
    const totalRealEstateProfit = realEstateAssets.reduce((sum, p) => sum + p.profitSale, 0);
    const realEstateTax = totalRealEstateProfit > 0 ? totalRealEstateProfit * 0.2 : 0;

    // 5. Total do DARF
    const totalDarf = stocksTax + commonTax + realEstateTax;

    return totalDarf;
  };

  const summaries = useMemo(() => {
    const totalProfit = planningResults.reduce((sum, item) => sum + (item.profitSale > 0 ? item.profitSale : 0), 0);
    const totalLoss = planningResults.reduce((sum, item) => sum + (item.profitSale < 0 ? item.profitSale : 0), 0);
    const netResult = planningResults.reduce((sum, item) => sum + item.profitSale, 0);
    const totalSaleValue = planningResults.reduce((sum, item) => sum + item.totalSale, 0);

    const estimatedTax = calculateEstimatedTax(planningResults);

    return {
      totalProfit,
      totalLoss,
      netResult,
      netResultText: netResult >= 0 ? 'Lucro' : 'Prejuízo',
      estimatedTax,
      totalSaleValue,
    };
  }, [planningResults]);

  const applyPlanningTemplate = (tabToApply) => {
    const results = [];

    switch (tabToApply) {
      case 'custom':
        equityPositions.forEach((position) => {
          const marketPrice = Number(position.product.marketPrice || 0);
          const quantity = Number(position.quantity || 0);
          const averagePrice = Number(position.averagePrice || 0);

          results.push({
            id: position.id,
            ticker: position.product.ticker,
            situation: position.situation,
            subtype: position.product.subtype,
            tSubtype: position.product.tSubtype,
            marketPrice,
            averagePrice,
            totalSale: quantity * marketPrice,
            profitSale: (marketPrice - averagePrice) * quantity,
            quantityToSell: quantity,
            maxQuantityToSell: quantity,
          });
        });
        break;

      case 'exemption_20k': {
        // Vende primeiro os ativos mais lucrativos para aproveitar ao máximo a isenção de 20k, depois os outros
        // lucrativos até atingir o limite
        const EXEMPTION_LIMIT = 19500;
        let remainingLimit = EXEMPTION_LIMIT;

        const allMapped = equityPositions.map((position) => {
          const marketPrice = Number(position.product.marketPrice || 0);
          const quantity = Number(position.quantity || 0);
          const averagePrice = Number(position.averagePrice || 0);
          return {
            id: position.id,
            ticker: position.product.ticker,
            situation: position.situation,
            subtype: position.product.subtype,
            tSubtype: position.product.tSubtype,
            marketPrice,
            averagePrice,
            maxQuantityToSell: quantity,
            quantityToSell: 0,
            totalSale: 0,
            profitSale: 0,
          };
        });

        const profitableStocks = allMapped
          .filter((p) => p.subtype.toLowerCase() === 'stock' && p.marketPrice > p.averagePrice)
          .sort((a, b) => {
            const profitA = (a.marketPrice - a.averagePrice) * a.maxQuantityToSell;
            const profitB = (b.marketPrice - b.averagePrice) * b.maxQuantityToSell;
            return profitB - profitA;
          });

        profitableStocks.forEach((stock) => {
          if (remainingLimit <= 0) return;
          const fullValue = stock.maxQuantityToSell * stock.marketPrice;
          if (fullValue <= remainingLimit) {
            stock.quantityToSell = stock.maxQuantityToSell;
            remainingLimit -= fullValue;
          } else {
            const possibleQty = Math.floor(remainingLimit / stock.marketPrice);
            stock.quantityToSell = possibleQty;
            remainingLimit = 0;
          }
          stock.totalSale = stock.quantityToSell * stock.marketPrice;
          stock.profitSale = (stock.marketPrice - stock.averagePrice) * stock.quantityToSell;
        });

        allMapped.forEach((p) => {
          const updated = profitableStocks.find((s) => s.id === p.id);
          results.push(updated || p);
        });
        break;
      }
      case 'offset_loss': {
        // Vende primeiro os prejuízos para criar um "buffer" de perdas, depois vende os lucrativos
        // dentro do mesmo bucket de tributação para tentar zerar esse buffer
        const allMapped = equityPositions.map((p) => {
          const marketPrice = Number(p.product.marketPrice || 0);
          const averagePrice = Number(p.averagePrice || 0);
          const quantity = Number(p.quantity || 0);
          const subtype = p.product.subtype.toLowerCase();

          const taxBucket = ['fii', 'fiagro'].includes(subtype) ? 20 : 15;

          return {
            id: p.id,
            ticker: p.product.ticker,
            subtype,
            tSubtype: p.product.tSubtype,
            taxBucket,
            marketPrice,
            averagePrice,
            maxQuantityToSell: quantity,
            quantityToSell: 0,
            totalSale: 0,
            profitSale: 0,
            profitPerUnit: marketPrice - averagePrice,
            situation: p.situation,
          };
        });

        [15, 20].forEach((currentBucket) => {
          const bucketAssets = allMapped.filter((p) => p.taxBucket === currentBucket);

          let lossBuffer = 0;
          bucketAssets.forEach((p) => {
            if (p.profitPerUnit < 0) {
              const totalLoss = p.maxQuantityToSell * p.profitPerUnit;

              // Prejuízos menores que a margem mínima ficam de fora: entre o cálculo do plano e a
              // execução da ordem, o preço pode subir e a venda acabar saindo em lucro.
              if (Math.abs(totalLoss) < minLossMargin) return;

              p.quantityToSell = p.maxQuantityToSell;
              p.totalSale = p.quantityToSell * p.marketPrice;
              p.profitSale = totalLoss;
              lossBuffer += Math.abs(p.profitSale);
            }
          });

          const profitableInBucket = bucketAssets
            .filter((p) => p.profitPerUnit > 0)
            .sort((a, b) => b.profitPerUnit * b.maxQuantityToSell - a.profitPerUnit * a.maxQuantityToSell);

          profitableInBucket.forEach((p) => {
            if (lossBuffer <= 0) return;

            const maxProfitPossible = p.maxQuantityToSell * p.profitPerUnit;

            if (maxProfitPossible <= lossBuffer) {
              p.quantityToSell = p.maxQuantityToSell;
              lossBuffer -= maxProfitPossible;
            } else {
              const qtyToOffset = Math.floor(lossBuffer / p.profitPerUnit);
              p.quantityToSell = qtyToOffset;
              lossBuffer = 0;
            }

            p.totalSale = p.quantityToSell * p.marketPrice;
            p.profitSale = p.quantityToSell * p.profitPerUnit;
          });
        });

        results.push(...allMapped);
        break;
      }
      case 'sell_priced':
        equityPositions.forEach((position) => {
          const marketPrice = Number(position.product.marketPrice || 0);
          const quantity = Number(position.quantity || 0);
          const averagePrice = Number(position.averagePrice || 0);

          // Vende apenas se houver preço médio vindo da API (> 0)
          const hasPrice = averagePrice > 0;
          const quantityToSell = hasPrice ? quantity : 0;

          results.push({
            id: position.id,
            ticker: position.product.ticker,
            situation: position.situation,
            subtype: position.product.subtype,
            tSubtype: position.product.tSubtype,
            marketPrice,
            averagePrice,
            totalSale: quantityToSell * marketPrice,
            profitSale: (marketPrice - averagePrice) * quantityToSell,
            quantityToSell,
            maxQuantityToSell: quantity,
          });
        });
        break;
      case 'liquidate_position':
        equityPositions.forEach((position) => {
          const marketPrice = Number(position.product.marketPrice || 0);
          const quantity = Number(position.quantity || 0);
          const averagePrice = Number(position.averagePrice || 0);

          // Vende 100% de tudo
          results.push({
            id: position.id,
            ticker: position.product.ticker,
            situation: position.situation,
            subtype: position.product.subtype,
            tSubtype: position.product.tSubtype,
            marketPrice,
            averagePrice,
            totalSale: quantity * marketPrice,
            profitSale: (marketPrice - averagePrice) * quantity,
            quantityToSell: quantity,
            maxQuantityToSell: quantity,
          });
        });
        break;

      default:
        console.log('Template desconhecido:', activeTab);
    }

    setPlanningResults(results);
    setSelectedIds(results.filter((r) => r.quantityToSell > 0).map((r) => r.id));
  };

  useEffect(() => {
    applyPlanningTemplate(activeTab);
  }, [equityPositions, minLossMargin]);

  const handleTabChange = (key) => {
    setActiveTab(key);
    applyPlanningTemplate(key);
  };

  const showCopiedFeedback = (outputType) => {
    setCopiedOutput(outputType);

    setTimeout(() => {
      setCopiedOutput(null);
    }, 2200);
  };

  const showCopyError = (error, fallbackMessage = 'Erro ao copiar conteúdo.') => {
    console.error(fallbackMessage, error);

    addToast({
      type: 'error',
      message: error?.message || fallbackMessage,
    });
  };

  const showNoRowsError = () => {
    addToast({
      type: 'error',
      message: 'Nenhum ativo selecionado para copiar.',
    });
  };

  const getRowsToCopy = () => {
    return planningResults.filter((position) => {
      return selectedIds.includes(position.id) && Number(position.quantityToSell || 0) > 0;
    });
  };

  const escapeHtml = (value) => {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  };

  const formatCurrencyHtml = (value) => {
    return escapeHtml(formatCurrency(Number(value || 0)));
  };

  const getResultColor = (value) => {
    const numericValue = Number(value || 0);

    if (numericValue > 0) return '#55A32A';
    if (numericValue < 0) return '#994E3C';

    return '#123B5A';
  };

  const getResultBgColor = (value) => {
    const numericValue = Number(value || 0);

    if (numericValue > 0) return '#E6F7F1';
    if (numericValue < 0) return '#FDECEC';

    return '#E8F2F3';
  };

  const buildSalesOrderTableHtml = (rows) => {
    const safeClientName = escapeHtml(clientName || 'Cliente');
    const safeAccountNumber = escapeHtml(accountNumber || '');

    const tableRows = rows
      .map((position) => {
        const ticker = escapeHtml(position.ticker || '');
        const quantity = Math.floor(Number(position.quantityToSell || 0));

        return `
        <tr>
          <td style="padding: 16px 22px; border-bottom: 1px solid #DDECEF; font-weight: 700; color: #123B5A; text-align: left;">
            ${ticker}
          </td>
          <td style="padding: 16px 18px; border-bottom: 1px solid #DDECEF; color: #123B5A; text-align: center;">
            V
          </td>
          <td style="padding: 16px 18px; border-bottom: 1px solid #DDECEF; color: #123B5A; text-align: center;">
            ${quantity}
          </td>
          <td style="padding: 16px 18px; border-bottom: 1px solid #DDECEF; color: #123B5A; text-align: center;">
            M
          </td>
          <td style="padding: 16px 18px; border-bottom: 1px solid #DDECEF; color: #123B5A; text-align: center;">
            ${safeAccountNumber}
          </td>
          <td style="padding: 16px 22px; border-bottom: 1px solid #DDECEF; color: #123B5A; text-align: center;">
            DIA
          </td>
        </tr>
      `;
      })
      .join('');

    return `
    <div style="width: 720px; background: #FFFFFF; font-family: Arial, Helvetica, sans-serif; color: #123B5A;">
      <div style="padding: 18px 16px 14px 16px;">
        <table style="width: 100%; border-collapse: collapse;">
          <tr>
            <td style="vertical-align: middle; text-align: left;">
              <img src="https://res.cloudinary.com/rio-claro-investimentos/image/upload/mqw2l1ufnmvkqvkqtfjb9tb0a0zf.png" alt="Rio Claro Investimentos" style="display:block;width:160px;height:auto;" />
            </td>

            <td style="vertical-align: middle; text-align: right;">
              <span
                style="
                  display: inline-block;
                  width: 22px;
                  height: 22px;
                  background-color: #39B8BE;
                  -webkit-mask: url('https://res.cloudinary.com/rio-claro-investimentos/image/upload/5i26i5ohlh362ug787phl9d8wngd.png') center / contain no-repeat;
                  mask: url('https://res.cloudinary.com/rio-claro-investimentos/image/upload/5i26i5ohlh362ug787phl9d8wngd.png') center / contain no-repeat;
                  vertical-align: middle;
                  margin-right: 8px;
                "
              ></span>
              <span style="font-size: 19px; font-weight: 700; color: #123B5A; vertical-align: middle;">
                ${safeClientName}
              </span>
            </td>
          </tr>
        </table>
      </div>

      <div style="margin: 0 16px; border: 1px solid #DDECEF; border-radius: 12px; overflow: hidden;">
        <table style="width: 100%; border-collapse: collapse; table-layout: fixed; font-size: 15px;">
          <colgroup>
            <col style="width: 150px;" />
            <col style="width: 70px;" />
            <col style="width: 90px;" />
            <col style="width: 90px;" />
            <col style="width: 180px;" />
            <col style="width: 110px;" />
          </colgroup>
          <thead>
            <tr>
              <th colspan="6" style="background: #3CB4B9; color: #FFFFFF; padding: 16px 12px; font-size: 16px; font-weight: 700; text-align: center; letter-spacing: 0.2px;">
                ORDEM SIMPLES DE COMPRA E VENDA *
              </th>
            </tr>

            <tr>
              <th style="background: #E8F2F3; color: #123B5A; padding: 14px 22px; font-size: 15px; font-weight: 700; text-align: left;">
                Ativo
              </th>
              <th style="background: #E8F2F3; color: #123B5A; padding: 14px 18px; font-size: 15px; font-weight: 700; text-align: center;">
                C/V
              </th>
              <th style="background: #E8F2F3; color: #123B5A; padding: 14px 18px; font-size: 15px; font-weight: 700; text-align: center;">
                Qtd.
              </th>
              <th style="background: #E8F2F3; color: #123B5A; padding: 14px 18px; font-size: 15px; font-weight: 700; text-align: center;">
                Preço
              </th>
              <th style="background: #E8F2F3; color: #123B5A; padding: 14px 18px; font-size: 15px; font-weight: 700; text-align: center;">
                Conta
              </th>
              <th style="background: #E8F2F3; color: #123B5A; padding: 14px 22px; font-size: 15px; font-weight: 700; text-align: center;">
                Validade
              </th>
            </tr>
          </thead>

          <tbody>
            ${tableRows}
          </tbody>
        </table>
      </div>

      <div style="padding: 18px 42px 10px 42px; font-size: 13px; line-height: 1.4; color: #4D6A7F; font-style: italic;">
        * Os dados apresentados são estimativas e não refletem valores reais de execução.
      </div>
    </div>
  `;
  };

  const buildSalesOrderTableText = (rows) => {
    const header = ['Ativo', 'C/V', 'Qtd.', 'Preço', 'Conta', 'Validade'].join('\t');

    const body = rows
      .map((position) => {
        return [
          position.ticker || '',
          'V',
          Math.floor(Number(position.quantityToSell || 0)),
          'M',
          accountNumber || '',
          'DIA',
        ].join('\t');
      })
      .join('\n');

    return `ORDEM SIMPLES DE COMPRA E VENDA\n${header}\n${body}`;
  };

  const copySalesOrderTable = async () => {
    if (isCopyingOutput) return;

    const rows = getRowsToCopy();

    if (rows.length === 0) {
      showNoRowsError();
      return;
    }

    setIsCopyingOutput(true);
    setCopiedOutput(null);

    const html = buildSalesOrderTableHtml(rows);
    const text = buildSalesOrderTableText(rows);

    try {
      if (navigator.clipboard && window.ClipboardItem) {
        const clipboardItem = new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([text], { type: 'text/plain' }),
        });

        await navigator.clipboard.write([clipboardItem]);
      } else {
        await navigator.clipboard.writeText(text);
      }

      showCopiedFeedback('table');
    } catch (error) {
      showCopyError(error, 'Erro ao copiar tabela.');
    } finally {
      setIsCopyingOutput(false);
    }
  };

  const buildSalesPlanningReportHtml = (rows) => {
    const safeClientName = escapeHtml(clientName || 'Cliente');

    const totalSaleValue = rows.reduce((sum, position) => {
      return sum + Number(position.totalSale || 0);
    }, 0);

    const totalProfitSale = rows.reduce((sum, position) => {
      return sum + Number(position.profitSale || 0);
    }, 0);

    const estimatedTax = calculateEstimatedTax(rows);

    const resultText = totalProfitSale >= 0 ? 'Lucro' : 'Prejuízo';
    const resultColor = getResultColor(totalProfitSale);
    const resultBgColor = getResultBgColor(totalProfitSale);

    const tableRows = rows
      .map((position) => {
        const ticker = escapeHtml(position.ticker || '');
        const type = escapeHtml(position.tSubtype || position.subtype || '');
        const quantity = Math.floor(Number(position.quantityToSell || 0));
        const averagePrice = formatCurrencyHtml(position.averagePrice);
        const marketPrice = formatCurrencyHtml(position.marketPrice);
        const totalSale = formatCurrencyHtml(position.totalSale);
        const profitSale = formatCurrencyHtml(position.profitSale);
        const profitColor = getResultColor(position.profitSale);

        return `
        <tr>
          <td style="padding: 14px 16px; border-bottom: 1px solid #DDECEF; font-weight: 700; color: #123B5A; text-align: left;">
            ${ticker}
          </td>
          <td style="padding: 14px 12px; border-bottom: 1px solid #DDECEF; color: #123B5A; text-align: center;">
            ${type}
          </td>
          <td style="padding: 14px 12px; border-bottom: 1px solid #DDECEF; color: #123B5A; text-align: center;">
            ${quantity}
          </td>
          <td style="padding: 14px 12px; border-bottom: 1px solid #DDECEF; color: #123B5A; text-align: right; white-space: nowrap;">
            ${averagePrice}
          </td>
          <td style="padding: 14px 12px; border-bottom: 1px solid #DDECEF; color: #123B5A; text-align: right; white-space: nowrap;">
            ${marketPrice}
          </td>
          <td style="padding: 14px 12px; border-bottom: 1px solid #DDECEF; color: #123B5A; text-align: right; font-weight: 700; white-space: nowrap;">
            ${totalSale}
          </td>
          <td style="padding: 14px 16px; border-bottom: 1px solid #DDECEF; color: ${profitColor}; text-align: right; font-weight: 700; white-space: nowrap;">
            ${profitSale}
          </td>
        </tr>
      `;
      })
      .join('');

    return `
    <div style="width: 980px; background: #FFFFFF; font-family: Arial, Helvetica, sans-serif; color: #123B5A;">
      <div style="padding: 18px 16px 14px 16px;">
        <table style="width: 100%; border-collapse: collapse;">
          <tr>
            <td style="vertical-align: middle; text-align: left;">
              <img src="https://res.cloudinary.com/rio-claro-investimentos/image/upload/mqw2l1ufnmvkqvkqtfjb9tb0a0zf.png" alt="Rio Claro Investimentos" style="display:block;width:160px;height:auto;" />
            </td>

            <td style="vertical-align: middle; text-align: right;">
              <span
                style="
                  display: inline-block;
                  width: 22px;
                  height: 22px;
                  background-color: #39B8BE;
                  -webkit-mask: url('https://res.cloudinary.com/rio-claro-investimentos/image/upload/5i26i5ohlh362ug787phl9d8wngd.png') center / contain no-repeat;
                  mask: url('https://res.cloudinary.com/rio-claro-investimentos/image/upload/5i26i5ohlh362ug787phl9d8wngd.png') center / contain no-repeat;
                  vertical-align: middle;
                  margin-right: 8px;
                "
              ></span>
              <span style="font-size: 19px; font-weight: 700; color: #123B5A; vertical-align: middle;">
                ${safeClientName}
              </span>
            </td>
          </tr>
        </table>
      </div>

      <div style="margin: 0 16px 14px 16px; border: 1px solid #DDECEF; border-radius: 12px; overflow: hidden;">
        <table style="width: 100%; border-collapse: collapse; table-layout: fixed; font-size: 14px;">
          <colgroup>
            <col style="width: 115px;" />
            <col style="width: 95px;" />
            <col style="width: 70px;" />
            <col style="width: 130px;" />
            <col style="width: 130px;" />
            <col style="width: 155px;" />
            <col style="width: 155px;" />
          </colgroup>
          <thead>
            <tr>
              <th colspan="7" style="background: #3CB4B9; color: #FFFFFF; padding: 16px 12px; font-size: 16px; font-weight: 700; text-align: center; letter-spacing: 0.2px;">
                RELATÓRIO DE PLANEJAMENTO DE VENDAS*
              </th>
            </tr>

            <tr>
              <th style="background: #E8F2F3; color: #123B5A; padding: 13px 16px; font-size: 14px; font-weight: 700; text-align: left;">
                Ativo
              </th>
              <th style="background: #E8F2F3; color: #123B5A; padding: 13px 12px; font-size: 14px; font-weight: 700; text-align: center;">
                Tipo
              </th>
              <th style="background: #E8F2F3; color: #123B5A; padding: 13px 12px; font-size: 14px; font-weight: 700; text-align: center;">
                Qtd.
              </th>
              <th style="background: #E8F2F3; color: #123B5A; padding: 13px 12px; font-size: 14px; font-weight: 700; text-align: right;">
                Pr Médio
              </th>
              <th style="background: #E8F2F3; color: #123B5A; padding: 13px 12px; font-size: 14px; font-weight: 700; text-align: right;">
                Pr Atual
              </th>
              <th style="background: #E8F2F3; color: #123B5A; padding: 13px 12px; font-size: 14px; font-weight: 700; text-align: right;">
                Total
              </th>
              <th style="background: #E8F2F3; color: #123B5A; padding: 13px 16px; font-size: 14px; font-weight: 700; text-align: right;">
                L/P
              </th>
            </tr>
          </thead>

          <tbody>
            ${tableRows}

            <tr>
              <td colspan="5" style="padding: 15px 16px; background: #F6FAFB; border-top: 1px solid #DDECEF; color: #123B5A; text-align: right; font-weight: 700;">
                Total
              </td>
              <td style="padding: 15px 12px; background: #F6FAFB; border-top: 1px solid #DDECEF; color: #123B5A; text-align: right; font-weight: 700; white-space: nowrap;">
                ${formatCurrencyHtml(totalSaleValue)}
              </td>
              <td style="padding: 15px 16px; background: #F6FAFB; border-top: 1px solid #DDECEF; color: ${resultColor}; text-align: right; font-weight: 700; white-space: nowrap;">
                ${formatCurrencyHtml(totalProfitSale)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div style="padding: 0 16px;">
        <table style="width: 100%; border-collapse: separate; border-spacing: 8px 0;">
          <tr>
            <td style="width: 33.33%; background: #F6FAFB; border: 1px solid #DDECEF; border-radius: 12px; padding: 14px 16px;">
              <div style="font-size: 12px; color: #4D6A7F; margin-bottom: 6px;">
                Total de vendas
              </div>
              <div style="font-size: 18px; font-weight: 700; color: #123B5A;">
                ${formatCurrencyHtml(totalSaleValue)}
              </div>
            </td>

            <td style="width: 33.33%; background: ${resultBgColor}; border: 1px solid #DDECEF; border-radius: 12px; padding: 14px 16px;">
              <div style="font-size: 12px; color: #4D6A7F; margin-bottom: 6px;">
                Resultado Planejado (${resultText})
              </div>
              <div style="font-size: 18px; font-weight: 700; color: ${resultColor};">
                ${formatCurrencyHtml(totalProfitSale)}
              </div>
            </td>

            <td style="width: 33.33%; background: #F6FAFB; border: 1px solid #DDECEF; border-radius: 12px; padding: 14px 16px;">
              <div style="font-size: 12px; color: #4D6A7F; margin-bottom: 6px;">
                DARF Estimado
              </div>
              <div style="font-size: 18px; font-weight: 700; color: #123B5A;">
                ${formatCurrencyHtml(estimatedTax)}
              </div>
            </td>
          </tr>
        </table>
      </div>

      <div style="padding: 18px 42px 10px 42px; font-size: 13px; line-height: 1.4; color: #4D6A7F; font-style: italic;">
        * Os valores apresentados são estimativas e não refletem valores reais de execução.
      </div>
    </div>
  `;
  };

  const buildSalesPlanningReportText = (rows) => {
    const totalSaleValue = rows.reduce((sum, position) => {
      return sum + Number(position.totalSale || 0);
    }, 0);

    const totalProfitSale = rows.reduce((sum, position) => {
      return sum + Number(position.profitSale || 0);
    }, 0);

    const estimatedTax = calculateEstimatedTax(rows);
    const resultText = totalProfitSale >= 0 ? 'Lucro' : 'Prejuízo';

    const header = ['Ativo', 'Tipo', 'Qtd.', 'Pr Médio', 'Pr Atual', 'Total', 'L/P'].join('\t');

    const body = rows
      .map((position) => {
        return [
          position.ticker || '',
          position.tSubtype || position.subtype || '',
          Math.floor(Number(position.quantityToSell || 0)),
          formatCurrency(Number(position.averagePrice || 0)),
          formatCurrency(Number(position.marketPrice || 0)),
          formatCurrency(Number(position.totalSale || 0)),
          formatCurrency(Number(position.profitSale || 0)),
        ].join('\t');
      })
      .join('\n');

    const totalRow = ['Total', '', '', '', '', formatCurrency(totalSaleValue), formatCurrency(totalProfitSale)].join(
      '\t'
    );

    return [
      'RELATÓRIO DE PLANEJAMENTO DE VENDAS*',
      clientName || 'Cliente',
      '',
      header,
      body,
      totalRow,
      '',
      `Total de vendas: ${formatCurrency(totalSaleValue)}`,
      `Resultado Planejado (${resultText}): ${formatCurrency(totalProfitSale)}`,
      `DARF Estimado: ${formatCurrency(estimatedTax)}`,
      '',
      '* Os valores apresentados são estimativas e não refletem valores reais de execução.',
    ].join('\n');
  };

  const copySalesPlanningReport = async () => {
    if (isCopyingOutput) return;

    const rows = getRowsToCopy();

    if (rows.length === 0) {
      showNoRowsError();
      return;
    }

    setIsCopyingOutput(true);
    setCopiedOutput(null);

    const html = buildSalesPlanningReportHtml(rows);
    const text = buildSalesPlanningReportText(rows);

    try {
      if (navigator.clipboard && window.ClipboardItem) {
        const clipboardItem = new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([text], { type: 'text/plain' }),
        });

        await navigator.clipboard.write([clipboardItem]);
      } else {
        await navigator.clipboard.writeText(text);
      }

      showCopiedFeedback('report_text');
    } catch (error) {
      showCopyError(error, 'Erro ao copiar relatório.');
    } finally {
      setIsCopyingOutput(false);
    }
  };

  const waitForImagesToLoad = async (element) => {
    const images = Array.from(element.querySelectorAll('img'));

    await Promise.all(
      images.map((img) => {
        if (img.complete) return Promise.resolve();

        return new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
        });
      })
    );
  };

  const copySalesPlanningReportImage = async () => {
    if (isCopyingOutput) return;

    const rows = getRowsToCopy();

    if (rows.length === 0) {
      showNoRowsError();
      return;
    }

    setIsCopyingOutput(true);
    setCopiedOutput(null);

    const html = buildSalesPlanningReportHtml(rows);

    const wrapper = document.createElement('div');

    wrapper.style.position = 'fixed';
    wrapper.style.left = '-1200px';
    wrapper.style.top = '0';
    wrapper.style.width = '980px';
    wrapper.style.background = '#FFFFFF';
    wrapper.style.pointerEvents = 'none';
    wrapper.style.zIndex = '999999';

    wrapper.innerHTML = html;

    document.body.appendChild(wrapper);

    const reportNode = wrapper.firstElementChild;

    try {
      await waitForImagesToLoad(reportNode);

      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }

      await new Promise((resolve) => requestAnimationFrame(resolve));
      await new Promise((resolve) => setTimeout(resolve, 200));

      const blob = await toBlob(reportNode, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: '#FFFFFF',
        width: reportNode.scrollWidth,
        height: reportNode.scrollHeight,
        style: {
          margin: '0',
          transform: 'none',
        },
      });

      if (!blob) {
        throw new Error('Não foi possível gerar a imagem do relatório.');
      }

      if (navigator.clipboard && window.ClipboardItem) {
        const clipboardItem = new ClipboardItem({
          'image/png': blob,
        });

        await navigator.clipboard.write([clipboardItem]);

        showCopiedFeedback('report_image');
      } else {
        throw new Error('Seu navegador não suporta copiar imagem para a área de transferência.');
      }
    } catch (error) {
      showCopyError(error, 'Erro ao copiar relatório em imagem.');
    } finally {
      document.body.removeChild(wrapper);
      setIsCopyingOutput(false);
    }
  };

  return (
    <div className="bg-white rounded-custom20 p-4 h-full flex flex-col gap-5">
      <div className="bg-blueLight items-center p-4 flex flex-row justify-between" style={{ borderRadius: '12px' }}>
        <div className="flex flex-row items-center gap-3">
          <Coins size={20} className="text-tealBase" strokeWidth={1.75} />
          <span className="text-blueDarker text-xl font-semibold">Planejamento de vendas</span>
        </div>

        <div className="flex items-center gap-2">
          {tabs.map((tab) => (
            <DashboardTab
              key={tab.key}
              label={tab.label}
              active={activeTab === tab.key}
              onClick={() => handleTabChange(tab.key)}
              style="small"
            />
          ))}
        </div>
      </div>

      {activeTab === 'offset_loss' && (
        <p className="text-xs text-blueSteel -mt-2">
          Considerando apenas posições com prejuízo a partir de {formatCurrency(minLossMargin)} (configurável em
          Configurações Globais).
        </p>
      )}

      <div className="grid grid-cols-4 gap-5">
        <SimpleInfo title="Lucro Total" value={summaries.totalProfit} />
        <SimpleInfo title="Prejuízo Total" value={summaries.totalLoss} />
        <SimpleInfo title={`Resultado Líquido (${summaries.netResultText})`} value={summaries.netResult} />
        <SimpleInfo title="DARF Estimado" value={summaries.estimatedTax} />
      </div>

      <div className="px-2 pt-1 pb-2">
        <div
          className="grid items-center gap-4 text-blueDarker text-sm font-semibold px-3 py-2"
          style={{ gridTemplateColumns: GRID }}
        >
          <CustomCheckbox checked={allSelected} indeterminate={someSelected} onChange={toggleAll} />

          <div>Ticker</div>
          <div>Tipo</div>
          <div>Pr Mercado</div>
          <div>Total Venda</div>
          <div>L/P da Venda</div>
          <div>Qtd Venda</div>
        </div>

        <div className="space-y-0 mt-1">
          {planningResults.map((position, idx) => (
            <SaleRow
              key={position.id}
              position={position}
              idx={idx}
              isSelected={selectedIds.includes(position.id)}
              onToggle={toggleOne}
              onQuantityToSellChange={handleQuantityToSellChange}
            />
          ))}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-5">
        <SimpleInfo title="Valor total de vendas" value={summaries.totalSaleValue} />
        <SimpleInfo title="Resultado Planejado" value={summaries.netResult} />
        <SimpleInfo title="DARF Estimado" value={summaries.estimatedTax} />
      </div>

      <div className="bg-blueLight items-center p-4 flex flex-row justify-between" style={{ borderRadius: '12px' }}>
        <div className="flex flex-row items-center gap-3">
          <Upload size={20} className="text-tealBase" strokeWidth={1.75} />
          <span className="text-blueDarker text-xl font-semibold">Exportar</span>
        </div>

        <div className="flex items-center gap-2">
          <div className="relative">
            <CustomButton
              disabled={isCopyingOutput}
              onClick={copySalesOrderTable}
              icon={Copy}
              className={isCopyingOutput ? 'opacity-60 cursor-not-allowed' : ''}
            >
              COPIAR TABELA
            </CustomButton>

            <CopiedTooltip visible={copiedOutput === 'table'} message="Tabela copiada para a área de transferência!" />
          </div>

          <div className="relative">
            <CustomButton
              disabled={isCopyingOutput}
              onClick={copySalesPlanningReport}
              icon={Copy}
              className={isCopyingOutput ? 'opacity-60 cursor-not-allowed' : ''}
            >
              COPIAR RELATÓRIO (TEXTO)
            </CustomButton>

            <CopiedTooltip
              visible={copiedOutput === 'report_text'}
              message="Relatório copiado para a área de transferência!"
            />
          </div>

          <div className="relative">
            <CustomButton
              disabled={isCopyingOutput}
              onClick={copySalesPlanningReportImage}
              icon={Copy}
              className={isCopyingOutput ? 'opacity-60 cursor-not-allowed' : ''}
            >
              COPIAR RELATÓRIO (IMAGEM)
            </CustomButton>

            <CopiedTooltip
              visible={copiedOutput === 'report_image'}
              message="Imagem copiada para a área de transferência!"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesPlanning;
